import { Node, Edge } from 'reactflow';
import { TriggerNodeData, ActionNodeData, ConditionNodeData, FilterNodeData } from '../types/nodes';

export interface ValidationError {
  nodeId?: string;
  edgeId?: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  suggestion?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  suggestions: ValidationError[];
}

export class FlowValidator {
  private nodes: Node[];
  private edges: Edge[];
  
  constructor(nodes: Node[], edges: Edge[]) {
    this.nodes = nodes;
    this.edges = edges;
  }
  
  validate(): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const suggestions: ValidationError[] = [];
    
    // 1. Verifica presenza di almeno un trigger
    const triggerNodes = this.nodes.filter(n => n.type === 'trigger');
    if (triggerNodes.length === 0) {
      errors.push({
        type: 'error',
        message: 'Il flusso deve avere almeno un nodo Trigger',
        suggestion: 'Aggiungi un nodo Trigger per iniziare il flusso'
      });
    }
    
    // 2. Verifica nodi isolati
    const connectedNodeIds = new Set<string>();
    this.edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    this.nodes.forEach(node => {
      if (!connectedNodeIds.has(node.id)) {
        warnings.push({
          nodeId: node.id,
          type: 'warning',
          message: `Il nodo "${node.data.label}" non è connesso`,
          suggestion: 'Collega questo nodo al flusso principale'
        });
      }
    });
    
    // 3. Verifica configurazione nodi
    this.nodes.forEach(node => {
      const validationResult = this.validateNodeConfiguration(node);
      errors.push(...validationResult.errors);
      warnings.push(...validationResult.warnings);
    });
    
    // 4. Verifica cicli infiniti
    const cycles = this.detectCycles();
    if (cycles.length > 0) {
      warnings.push({
        type: 'warning',
        message: 'Rilevati possibili cicli infiniti nel flusso',
        suggestion: 'Assicurati di avere condizioni di uscita nei cicli'
      });
    }
    
    // 5. Suggerimenti per ottimizzazione
    this.generateOptimizationSuggestions(suggestions);
    
    // 6. Verifica coerenza delle condizioni
    const conditionNodes = this.nodes.filter(n => n.type === 'condition');
    conditionNodes.forEach(node => {
      const outgoingEdges = this.edges.filter(e => e.source === node.id);
      if (outgoingEdges.length < 2) {
        warnings.push({
          nodeId: node.id,
          type: 'warning',
          message: `Il nodo condizione "${node.data.label}" ha meno di 2 uscite`,
          suggestion: 'Le condizioni dovrebbero avere almeno 2 percorsi (vero/falso)'
        });
      }
    });
    
    // 7. Verifica Router
    const routerNodes = this.nodes.filter(n => n.type === 'router');
    routerNodes.forEach(node => {
      const outgoingEdges = this.edges.filter(e => e.source === node.id);
      const expectedRoutes = node.data.config?.routes?.length || 0;
      if (outgoingEdges.length !== expectedRoutes) {
        warnings.push({
          nodeId: node.id,
          type: 'warning',
          message: `Il router "${node.data.label}" ha ${outgoingEdges.length} connessioni ma ${expectedRoutes} route configurate`,
          suggestion: 'Assicurati che ogni route sia connessa'
        });
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions
    };
  }
  
  private validateNodeConfiguration(node: Node): { errors: ValidationError[], warnings: ValidationError[] } {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    
    switch (node.type) {
      case 'trigger':
        const triggerData = node.data as TriggerNodeData;
        if (triggerData.triggerType === 'keyword' && !triggerData.config.keyword) {
          errors.push({
            nodeId: node.id,
            type: 'error',
            message: `Il trigger "${node.data.label}" richiede una parola chiave`,
            suggestion: 'Configura la parola chiave nel nodo'
          });
        }
        if (triggerData.triggerType === 'schedule' && !triggerData.config.schedule) {
          errors.push({
            nodeId: node.id,
            type: 'error',
            message: `Il trigger "${node.data.label}" richiede una programmazione`,
            suggestion: 'Configura gli orari di attivazione'
          });
        }
        break;
        
      case 'action':
        const actionData = node.data as ActionNodeData;
        if (actionData.actionType === 'send_message' && !actionData.config.message) {
          warnings.push({
            nodeId: node.id,
            type: 'warning',
            message: `L'azione "${node.data.label}" non ha un messaggio configurato`,
            suggestion: 'Aggiungi il testo del messaggio'
          });
        }
        if (actionData.actionType === 'send_media' && !actionData.config.mediaUrl) {
          errors.push({
            nodeId: node.id,
            type: 'error',
            message: `L'azione "${node.data.label}" richiede un URL media`,
            suggestion: 'Configura l\'URL del file media'
          });
        }
        if (actionData.actionType === 'wait' && !actionData.config.waitTime) {
          errors.push({
            nodeId: node.id,
            type: 'error',
            message: `L'azione "${node.data.label}" richiede un tempo di attesa`,
            suggestion: 'Configura la durata dell\'attesa'
          });
        }
        break;
        
      case 'condition':
        const conditionData = node.data as ConditionNodeData;
        if (!conditionData.config.value && !conditionData.config.regex) {
          warnings.push({
            nodeId: node.id,
            type: 'warning',
            message: `La condizione "${node.data.label}" non ha criteri configurati`,
            suggestion: 'Configura i criteri di valutazione'
          });
        }
        break;
    }
    
    return { errors, warnings };
  }
  
  private detectCycles(): string[][] {
    const cycles: string[][] = [];
    const visited = new Set<string>();
    const stack = new Set<string>();
    
    const dfs = (nodeId: string, path: string[]): void => {
      if (stack.has(nodeId)) {
        const cycleStart = path.indexOf(nodeId);
        cycles.push(path.slice(cycleStart));
        return;
      }
      
      if (visited.has(nodeId)) return;
      
      visited.add(nodeId);
      stack.add(nodeId);
      path.push(nodeId);
      
      const outgoingEdges = this.edges.filter(e => e.source === nodeId);
      for (const edge of outgoingEdges) {
        dfs(edge.target, [...path]);
      }
      
      stack.delete(nodeId);
    };
    
    this.nodes.forEach(node => {
      if (!visited.has(node.id)) {
        dfs(node.id, []);
      }
    });
    
    return cycles;
  }
  
  private generateOptimizationSuggestions(suggestions: ValidationError[]): void {
    // Suggerimento per delay nodes
    const actionNodes = this.nodes.filter(n => n.type === 'action');
    const consecutiveMessages = this.findConsecutiveNodes(actionNodes, 'send_message');
    
    if (consecutiveMessages.length > 2) {
      suggestions.push({
        type: 'info',
        message: 'Considera l\'aggiunta di ritardi tra messaggi consecutivi',
        suggestion: 'Aggiungi nodi Delay per evitare spam'
      });
    }
    
    // Suggerimento per analytics
    if (this.nodes.filter(n => n.type === 'analytics').length === 0) {
      suggestions.push({
        type: 'info',
        message: 'Aggiungi nodi Analytics per monitorare le performance',
        suggestion: 'Traccia eventi importanti nel tuo flusso'
      });
    }
    
    // Suggerimento per gestione errori
    const integrationNodes = this.nodes.filter(n => n.type === 'integration');
    if (integrationNodes.length > 0) {
      const hasErrorHandling = integrationNodes.some(node => {
        const outgoingEdges = this.edges.filter(e => e.source === node.id);
        return outgoingEdges.length > 1; // Assume multiple outputs = error handling
      });
      
      if (!hasErrorHandling) {
        suggestions.push({
          type: 'info',
          message: 'Considera la gestione degli errori per le integrazioni',
          suggestion: 'Aggiungi percorsi alternativi in caso di fallimento'
        });
      }
    }
    
    // Suggerimento per template
    const messageActions = actionNodes.filter(n => 
      n.data.actionType === 'send_message' && n.data.config.message?.length > 200
    );
    
    if (messageActions.length > 0) {
      suggestions.push({
        type: 'info',
        message: 'Usa template per messaggi lunghi o ripetitivi',
        suggestion: 'I template migliorano la manutenibilità'
      });
    }
  }
  
  private findConsecutiveNodes(nodes: Node[], actionType: string): Node[] {
    const consecutive: Node[] = [];
    let current: Node | null = null;
    
    for (const node of nodes) {
      if (node.data.actionType === actionType) {
        if (current) {
          const edge = this.edges.find(e => 
            e.source === current!.id && e.target === node.id
          );
          if (edge) {
            consecutive.push(node);
          } else {
            consecutive.length = 0;
          }
        }
        current = node;
      } else {
        current = null;
        consecutive.length = 0;
      }
    }
    
    return consecutive;
  }
}

// Helper function per ottenere suggerimenti intelligenti durante la costruzione del flusso
export function getSmartSuggestions(
  nodes: Node[], 
  edges: Edge[], 
  currentNode?: Node
): string[] {
  const suggestions: string[] = [];
  
  if (!currentNode) {
    if (nodes.length === 0) {
      suggestions.push('Inizia con un nodo Trigger per definire quando si attiva l\'automazione');
    }
    return suggestions;
  }
  
  const nodeType = currentNode.type;
  const outgoingEdges = edges.filter(e => e.source === currentNode.id);
  
  switch (nodeType) {
    case 'trigger':
      if (outgoingEdges.length === 0) {
        suggestions.push('Aggiungi un\'azione o una condizione dopo il trigger');
        suggestions.push('Considera l\'aggiunta di un filtro per limitare l\'esecuzione');
      }
      break;
      
    case 'action':
      const actionType = currentNode.data.actionType;
      if (actionType === 'send_message' && outgoingEdges.length === 0) {
        suggestions.push('Aggiungi un ritardo prima del prossimo messaggio');
        suggestions.push('Considera l\'aggiunta di una condizione per percorsi multipli');
      }
      if (actionType === 'request_payment') {
        suggestions.push('Aggiungi una condizione per verificare il pagamento');
      }
      break;
      
    case 'condition':
      if (outgoingEdges.length < 2) {
        suggestions.push('Aggiungi almeno due percorsi: uno per "vero" e uno per "falso"');
      }
      break;
      
    case 'integration':
      if (outgoingEdges.length === 1) {
        suggestions.push('Considera l\'aggiunta di un percorso per gestire gli errori');
      }
      break;
  }
  
  // Suggerimenti generali basati sul contesto
  const hasAnalytics = nodes.some(n => n.type === 'analytics');
  if (!hasAnalytics && nodes.length > 5) {
    suggestions.push('Aggiungi nodi Analytics per tracciare le metriche del flusso');
  }
  
  return suggestions;
}
